import React, {useEffect, useRef} from 'react';
import {ReportFileType} from "../graphql/queries/getReportFileListingForWms";
import {ReportFileFiltersType} from "../hooks/useWmsReportFileQuery";
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import DateRangeFilterItem from "../../../delivery/components/DateRangeFilterItem";
import { useTranslation } from 'react-i18next';
import {ReportFileTypeEnum} from "../../../../enums/ReportFileEnum";
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { getLastWeekDateRange } from '../../../../utils/dateTimeUtils';
import { useAutoClearDefaultTime } from '../../../../hooks/useAutoClearDefaultTime';
import { isReportDownloadSelfOnlyRole } from '../../../../utils/auth';

interface WmsReportFilterProps {
    filters: ReportFileFiltersType;
    setFilters: (filters: ReportFileFiltersType) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    selectedRows: ReportFileType[];
    operatorOptions: {value: string, label: string}[];
    reportTypeOptions?: {value: string, label: string}[];
}

const WmsReportFileFilter: React.FC<WmsReportFilterProps> = ({
    filters,
    setFilters,
    handleFilter,
    handleResetFilters,
    selectedRows,
    operatorOptions,
    reportTypeOptions: externalReportTypeOptions,
}) => {
    const {t} = useTranslation();
    const operatorLockedToSelf = isReportDownloadSelfOnlyRole();
    // 该组件本身未直接使用这些 hook（label/container 由通用筛选组件处理）
    
    // 默认日期不写入 URL/hash：这里仅用于 UI 默认展示
    // - undefined：未初始化，默认最近一周
    // - null：用户手动清空（点击叉号），保持为空
    // 一旦用户动过时间（包含点叉号清空），在 reset/刷新前都不再自动清空默认时间
    const hasUserEditedReportDateRef = useRef(false);
    useAutoClearDefaultTime({
        filters,
        setFilters,
        timeKey: 'reportDate',
        hasUserEditedTimeRef: hasUserEditedReportDateRef,
        // wms/download 页面会在 mount 后通过 useEffect 自动补 operatorId/type 等非时间筛选，
        // 避免因此把默认时间立刻清空（表现为默认时间一闪而过）
        skipFirstNonTimeChange: true,
    });

    const getEffectiveReportDate = (reportDate: any): [string | null, string | null] | null => {
        if (reportDate === null) return null;
        const isValid = reportDate &&
            Array.isArray(reportDate) &&
            reportDate.length === 2;
        return isValid ? (reportDate as [string | null, string | null]) : getLastWeekDateRange();
    };

    // 定义每列的标签数组，用于列对齐计算
    const column1Labels = [t('wms.download.filters.reportDate'), t('wms.download.filters.operator')];
    const column2Labels = [t('wms.download.filters.reportName')];
    const column3Labels = [t('wms.download.filters.type')];

    const defaultReportTypeOptions = [
        { value: '', label: t('enum.reportTypeOptions.all') },
        { value: '1', label: t('enum.reportTypeOptions.daily') },
        { value: '2', label: t('enum.reportTypeOptions.weekly') },
        { value: '3', label: t('enum.reportTypeOptions.monthly') },
        { value: '4', label: t('enum.reportTypeOptions.parcelDownload') },
        { value: '9', label: t('enum.reportTypeOptions.dashboardExport') },
        { value: '18', label: t('enum.reportTypeOptions.exceptionDownload') },
        { value: '5', label: t('enum.reportTypeOptions.otherCarrierBagTask') },
        { value: '22', label: t('enum.reportTypeOptions.bagTagExport') },
        { value: '23', label: t('enum.reportTypeOptions.warehouseLocationExport') },
        { value: '24', label: t('enum.reportTypeOptions.deliveryTaskTrackingNumberExport') },
        { value: ReportFileTypeEnum.DSP_SUMMARY_EXPORT.toString(), label: t('enum.reportTypeOptions.dspSummaryExport') },
        { value: ReportFileTypeEnum.ROUTE_ORDER_POOL_EXPORT.toString(), label: t('enum.reportTypeOptions.routeOrderPoolExport') },
        { value: ReportFileTypeEnum.PARTNER_PENALTY_EXPORT.toString(), label: t('enum.reportTypeOptions.partnerPenaltyExport') },
    ];
    const reportTypeOptions = externalReportTypeOptions || defaultReportTypeOptions;

    // 处理报表类型变化
    const handleTypeChange = (value: string) => {
        setFilters({...filters, type: value ? parseInt(value) : undefined});
    };

    // 处理操作人变化
    const handleOperatorChange = (value: string) => {
        setFilters({...filters, operatorId: value ? parseInt(value) : undefined});
    };

    // 处理报表名称变化
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters({...filters, name: e.target.value || undefined});
    };

    // 处理报表变化
    const handleReportDateChange = (
        dates: [string | null, string | null] | null
    ) => {
        hasUserEditedReportDateRef.current = true;
        setFilters({...filters, reportDate: dates});
    };


    return (
        <div className="mb-2 pl-0 py-2">
            {/* 使用 Grid 布局确保列对齐 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                {/* 创建时间筛选 */}
                <div className="min-w-0">
                    <DateRangeFilterItem
                        label={t('wms.download.filters.reportDate')}
                        value={getEffectiveReportDate(filters.reportDate)}
                        onChange={handleReportDateChange}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>
                {/* 报表名称筛选 */}
                <div className="min-w-0">
                    <InputFilterItem
                        label={t('wms.download.filters.reportName')}
                        value={filters.name?.toString() || ''}
                        onChange={handleNameChange}
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>

                {/* 报表类型筛选 */}
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('wms.download.filters.type')}
                        value={filters.type?.toString() || ''}
                        onChange={handleTypeChange}
                        options={reportTypeOptions}
                        placeholder={t('wms.download.filters.all') || undefined}
                        spanClassName={getSmartLabelClassName(column3Labels)}
                    />
                </div>
                {/* 第二行 */}
                {/* 操作人筛选 */}
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('wms.download.filters.operator')}
                        value={filters.operatorId?.toString() || ''}
                        onChange={handleOperatorChange}
                        options={operatorOptions}
                        placeholder={t('wms.download.filters.all') || undefined}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                        disabled={operatorLockedToSelf}
                        allowClear={!operatorLockedToSelf}
                        showSearch={!operatorLockedToSelf}
                    />
                </div>
                {/* 空占位，保持布局一致 */}
                <div className="min-w-0"></div>
                <div className="min-w-0"></div>
            </div>
        </div>
    );
};

export default WmsReportFileFilter;